import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import MissionService from '../../../../../../service/MissionService';
import MissionModelService from '../../../../../../service/MissionModelService';
import { useMDCockpit } from '../../../hooks/useMDCockpit';
import { showToastMessage } from '../../../../../../utils/commonFunctions';
import { epochToDisplay } from '../../../lib/datetime';
import { useCesium } from '../../../context/CesiumContext';
import { useDraggable } from '../../../hooks/useDraggable';
import { applyOrbitReportUpdatesToTreeItems, mapApiSatToTreeItem } from '../../../lib/missionSimulationMapper';
import CollapsibleSection from '../../Common/components/shared/CollapsibleSection';
import PropertyRow from './PropertyRow';
import { MODELLING_ORBIT_COLORS, MODELLING_DEFAULT_ORBIT_COLOR } from '../../../../../modelling/constants';
import BusSection from './BusSection';
import SensorDetailsPanel from './SensorDetailsPanel';
import type { SensorData } from '../../../types/sensor';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import styles from './PropertyPanel.module.scss';
// import addSatStyles from './LeftPanel/satellites/modals/AddSatModal.module.scss'; // TODO: Use AddSatModal styles from the modal itself // TODO: Use only needed styles or import from LeftPanel index;
import type { Satellite, SatGroup } from '../../../types/mission';
import { CloseSvg, ToggleMinimizeSvg } from '../../../../../CommonComponents/svgs/Svgs';

const SHOW_ORBIT_COLOR_PICKER = true;

const SmallChevronIcon = () => (
  <svg width="9" height="9" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path d="M2.4 3.8 5 6.2l2.6-2.4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Default physical characteristics per new satellite
const DEFAULT_PHYS = {
  mass: '12.4', volume: '0.04', dragCoeff: '2.2',
  dragArea: '0.04', reflectCoeff: '1.3', srpArea: '0.04',
};

type LiveOrbitState = {
  latitudeDeg: number;
  longitudeDeg: number;
  altitudeKm: number;
  velocityKmS: number | null;
  epochSec: number;
  semiMajorAxis?: number | null;
  eccentricity?: number | null;
  inclination?: number | null;
  raan?: number | null;
  aop?: number | null;
  ta?: number | null;
  meanAnomaly?: number | null;
};

const ORBIT_FIELD_KEYS = new Set([
  'altitude', 'inclination', 'raan', 'aop', 'ta', 'epoch',
  'ltan', 'longitude', 'perigee', 'apogee', 'eccentricity', 'groupNumSats',
]);

function getOrbitPropagationStatus(sat: Satellite | null, isTleSat: boolean): { badge: string; label: string } {
  if (isTleSat) return { badge: 'SGP4', label: 'TLE / SGP4' };
  if (sat?.ephemeris?.length) return { badge: 'PERTURBED', label: 'Perturbation applied' };
  if (sat?.propagationStatus === 'propagating') return { badge: 'PROPAGATING', label: 'Propagating' };
  if (sat?.propagationStatus === 'failed') return { badge: 'FAILED', label: 'Propagation failed' };
  return { badge: 'KEPLERIAN', label: 'Keplerian preview' };
}

function parseEpochDisplay(display: string): number {
  if (!display || display === '—') return 0;
  const cleaned = display.replace(' UTC', 'Z').replace(' ', 'T');
  const ts = new Date(cleaned).getTime();
  return isNaN(ts) ? 0 : ts / 1000;
}

function getSunRtAscension(epochDate: Date): number {
  const j2000 = Date.UTC(2000, 0, 1, 12, 0, 0);
  const diffDays = (epochDate.getTime() - j2000) / 86400000;
  let L = (280.460 + 0.9856474 * diffDays) % 360;
  if (L < 0) L += 360;
  let g = (357.528 + 0.9856003 * diffDays) % 360;
  if (g < 0) g += 360;
  const g_rad = g * Math.PI / 180;
  const lambda = L + 1.915 * Math.sin(g_rad) + 0.020 * Math.sin(2 * g_rad);
  const lambda_rad = lambda * Math.PI / 180;
  const epsilon_rad = 23.439 * Math.PI / 180;
  const ra_rad = Math.atan2(Math.cos(epsilon_rad) * Math.sin(lambda_rad), Math.cos(lambda_rad));
  let ra_deg = ra_rad * 180 / Math.PI;
  if (ra_deg < 0) ra_deg += 360;
  return ra_deg;
}

function ltanToRaan(ltan: string, epoch: string, epochTz: 'UTC' | 'IST' = 'UTC'): number {
  const suffix = epochTz === 'IST' ? '+05:30' : 'Z';
  const epochDate = new Date(epoch.replace(' ', 'T') + suffix);
  if (isNaN(epochDate.getTime())) return 0;
  const sunRaDeg = getSunRtAscension(epochDate);
  const parts = ltan.split(':').map(Number);
  const ltanHours = (parts[0] || 0) + (parts[1] || 0) / 60 + (parts[2] || 0) / 3600;
  return ((ltanHours - 12) * 15 + sunRaDeg + 720) % 360;
}

const formatEpochInput = (value: string) => value.replace(' ', 'T').slice(0, 19);
const formatEpochDisplayInput = (value: string) => value.replace('T', ' ');

function mapSensorToApi(s: SensorData, includeSensorId = false) {
  const pointingModeMap: Record<string, string> = {
    az: 'az-ele', dcm: 'body-axis', euler: 'euler', nadir: 'body-axis',
  };
  const lightingCondition = { sunlit: false, eclipse: false, umbra: false, penumbra: false };
  s.lighting.forEach(k => { if (k in lightingCondition) (lightingCondition as Record<string, boolean>)[k] = true; });

  return {
    ...(includeSensorId && s.sensorId ? { sensorId: s.sensorId } : {}),
    name: s.name ?? '',
    geometry: {
      type: s.geometry,
      conicalFov: parseFloat(s.fov) || 0,
      HFOV: parseFloat(s.fovH) || 0,
      VFOV: parseFloat(s.fovV) || 0,
    },
    pointingAxis: {
      mode: pointingModeMap[s.pointingMode] ?? 'body-axis',
      bodyAxis: {
        x: parseFloat(s.dcmX) || s.bodyAxis?.[0] || 0,
        y: parseFloat(s.dcmY) || s.bodyAxis?.[1] || 0,
        z: parseFloat(s.dcmZ) || s.bodyAxis?.[2] || 0,
      },
      euler: { roll: parseFloat(s.roll) || 0, pitch: parseFloat(s.pitch) || 0, yaw: parseFloat(s.yaw) || 0 },
      azEle: { azimuth: parseFloat(s.azimuth) || 0, elevation: parseFloat(s.elevation) || 0 },
    },
    lightingCondition,
  };
}

function mapApiSensorToLocal(sensor: any, fallback: SensorData): SensorData {
  const geo = sensor?.geometry ?? {};
  const axis = sensor?.pointingAxis ?? {};
  const lightingCondition = sensor?.lightingCondition ?? {};
  const modeMap: Record<string, SensorData['pointingMode']> = {
    'az-ele': 'az',
    'body-axis': 'dcm',
    euler: 'euler',
  };

  return {
    ...fallback,
    sensorId: sensor?.sensorId ?? fallback.sensorId,
    name: sensor?.name ?? fallback.name,
    geometry: geo.type ?? fallback.geometry,
    fov: String(geo.conicalFov ?? fallback.fov),
    fovH: String(geo.HFOV ?? fallback.fovH),
    fovV: String(geo.VFOV ?? fallback.fovV),
    pointingMode: modeMap[axis.mode] ?? fallback.pointingMode,
    dcmX: String(axis.bodyAxis?.x ?? fallback.dcmX),
    dcmY: String(axis.bodyAxis?.y ?? fallback.dcmY),
    dcmZ: String(axis.bodyAxis?.z ?? fallback.dcmZ),
    roll: String(axis.euler?.roll ?? fallback.roll),
    pitch: String(axis.euler?.pitch ?? fallback.pitch),
    yaw: String(axis.euler?.yaw ?? fallback.yaw),
    azimuth: String(axis.azEle?.azimuth ?? fallback.azimuth),
    elevation: String(axis.azEle?.elevation ?? fallback.elevation),
    lighting: (['sunlit', 'eclipse', 'umbra', 'penumbra'] as const).filter(k => lightingCondition[k]),
  };
}

function withFreshSensorId(sensor: SensorData): SensorData {
  return {
    ...sensor,
    sensorId: globalThis.crypto?.randomUUID?.() ?? `sensor-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  };
}

type GroupSensorTarget = {
  satId: string;
  satName: string;
  sensorIndex: number;
  sensor: SensorData;
};

type GroupSensorSummary = {
  id: string;
  label: string;
  targets: GroupSensorTarget[];
};

const parseGroupSensorFieldKey = (key: string): { summaryId: string; field: keyof SensorData } | null => {
  if (!key.startsWith('groupSensor::')) return null;
  const [, encodedSummaryId, field] = key.split('::');
  if (!encodedSummaryId || !field) return null;
  return { summaryId: decodeURIComponent(encodedSummaryId), field: field as keyof SensorData };
};

const getMixedSensorValue = (targets: GroupSensorTarget[], field: keyof SensorData): string => {
  const vals = targets.map(target => String(target.sensor[field] ?? ''));
  if (vals.length === 0) return '';
  return vals.every(value => value === vals[0]) ? vals[0] : 'Mixed';
};

const getGroupSensorIdentity = (sensor: SensorData) => {
  const name = sensor.name?.trim().toLowerCase();
  if (name) return `name:${name}`;
  return [
    'shape',
    sensor.geometry ?? '',
    sensor.fov ?? '',
    sensor.fovH ?? '',
    sensor.fovV ?? '',
    sensor.pointingMode ?? '',
  ].join(':').toLowerCase();
};

const buildGroupSensorSummaries = (children: Satellite[]): GroupSensorSummary[] => {
  const sensorIdCounts = new Map<string, number>();
  children.forEach(child => (child.sensors ?? []).forEach(sensor => {
    if (!sensor.sensorId) return;
    sensorIdCounts.set(sensor.sensorId, (sensorIdCounts.get(sensor.sensorId) ?? 0) + 1);
  }));

  const map = new Map<string, GroupSensorSummary>();
  children.forEach(child => (child.sensors ?? []).forEach((sensor, sensorIndex) => {
    const sharedSensorId = sensor.sensorId && (sensorIdCounts.get(sensor.sensorId) ?? 0) > 1;
    const summaryId = sharedSensorId ? `sensor:${sensor.sensorId}` : getGroupSensorIdentity(sensor);
    const existing = map.get(summaryId);
    const target: GroupSensorTarget = { satId: child.id, satName: child.name, sensorIndex, sensor };
    if (existing) {
      existing.targets.push(target);
      return;
    }
    map.set(summaryId, {
      id: summaryId,
      label: sensor.name || sensor.geometry || `Sensor ${sensorIndex + 1}`,
      targets: [target],
    });
  }));

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const buildGroupSensorDisplayList = (summaries: GroupSensorSummary[]): SensorData[] => (
  summaries.map((summary) => {
    const base = summary.targets[0]?.sensor;
    return {
      ...base,
      name: getMixedSensorValue(summary.targets, 'name'),
      geometry: getMixedSensorValue(summary.targets, 'geometry') as SensorData['geometry'],
      fov: getMixedSensorValue(summary.targets, 'fov'),
      fovH: getMixedSensorValue(summary.targets, 'fovH'),
      fovV: getMixedSensorValue(summary.targets, 'fovV'),
      pointingMode: getMixedSensorValue(summary.targets, 'pointingMode') as SensorData['pointingMode'],
    };
  }).filter(Boolean)
);

const getSensorForGroupChild = (summary: GroupSensorSummary, childId: string) =>
  summary.targets.find(target => target.satId === childId);

const PropertyPanel: React.FC = () => {
  const {
    selectedItemId,
    selectedItemType,
    orbitalEditTargetId,
    satellites,
    setSatellites,
    selectTreeItem,
    updateSatellite,
    analyserOpen,
    coverageAnalyserOpen,
    timelineCollapsed,
    timelineHeight,
    shouldBlockUI,
    leftPanelCollapsed,
    modelData,
    invalidateAccessWindows,
  } = useMDCockpit();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();
  const cesium = useCesium();
  const { getLiveSatelliteState } = cesium;
  const { style: dragStyle, headerProps: dragHeaderProps, pos: dragPos } = useDraggable({
    top: 60,
    left: leftPanelCollapsed ? 12 : 236,
  }, Boolean(selectedItemId));
  const timelineVisibleHeight = timelineCollapsed ? 34 : timelineHeight;
  const panelBottomOffset = timelineVisibleHeight + 40;
  const propertyPanelStyle = {
    ...dragStyle,
    '--property-panel-max-height': `calc(100vh - 60px - ${panelBottomOffset}px)`,
  } as React.CSSProperties;

  const groupEditMode = selectedItemType === 'group' && orbitalEditTargetId === selectedItemId;
  const sensorPanelStyle = {
    top: dragPos.top,
    left: dragPos.left + (groupEditMode ? 350 : 320) + 6,
  };

  // Per-satellite sensor storage: keyed by satellite ID
  const [sensorsBySat, setSensorsBySat] = useState<Record<string, SensorData[]>>({});
  const [sensorPanelOpen, setSensorPanelOpen] = useState(false);
  const [sensorPanelMode, setSensorPanelMode] = useState<'add' | 'edit' | 'paste'>('add');
  const [sensorClipboard, setSensorClipboard] = useState<SensorData[] | null>(null);
  const [editingSensorIndex, setEditingSensorIndex] = useState<number | null>(null);
  const [orbitEditMode, setOrbitEditMode] = useState(false);
  const [liveOrbit, setLiveOrbit] = useState<LiveOrbitState | null>(null);
  const [saving, setSaving] = useState(false);
  const [panelMinimized, setPanelMinimized] = useState(false);

  // Per-satellite editable field overrides: { satId: { fieldKey: editedValue } }
  const [fieldsBySat, setFieldsBySat] = useState<Record<string, Record<string, string>>>({});

  // Per-satellite committed sensor snapshot (for Cancel support)
  const [committedSensorsBySat, setCommittedSensorsBySat] = useState<Record<string, SensorData[]>>({});

  // Derive the active sensor list for the currently selected satellite
  const sensors: SensorData[] = (selectedItemId ? sensorsBySat[selectedItemId] : undefined) ?? [];

  const findSatelliteById = (satId: string): Satellite | null => {
    for (const item of satellites) {
      if ((item as SatGroup).type === 'group') {
        const child = (item as SatGroup).children.find((c: Satellite) => c.id === satId);
        if (child) return child;
      } else if ((item as Satellite).id === satId) {
        return item as Satellite;
      }
    }
    return null;
  };

  const findGroupById = (groupId: string): SatGroup | null => {
    return satellites.find(
      (item): item is SatGroup => (item as SatGroup).type === 'group' && item.id === groupId
    ) ?? null;
  };

  const getSatelliteSensors = (satId: string): SensorData[] => {
    return sensorsBySat[satId] ?? findSatelliteById(satId)?.sensors ?? [];
  };

  const getSensorApiContext = (satId: string) => {
    const sat = findSatelliteById(satId);
    if (!mission_id || !scenario_id) throw new Error('Missing mission or scenario id');
    if (!sat?.simulationGroupId) throw new Error('Missing satellite group id');
    return { sat, missionId: mission_id, scenarioId: scenario_id, groupId: sat.simulationGroupId };
  };

  const refreshSatellites = async () => {
    if (!mission_id || !scenario_id) return;
    const [groupRes, orbitRes] = await Promise.allSettled([
      MissionService.getSatelliteGroups(mission_id, scenario_id),
      MissionService.getAllSatelliteGroupOrbit(mission_id, scenario_id),
    ]);
    if (groupRes.status !== 'fulfilled') return;
    const list: any[] = groupRes.value?.data?.data ?? groupRes.value?.data ?? [];
    const reports: any[] = orbitRes.status === 'fulfilled'
      ? orbitRes.value?.data?.data ?? orbitRes.value?.data ?? []
      : [];
    const mapped = Array.isArray(list) ? list.map(mapApiSatToTreeItem) : [];
    setSatellites(Array.isArray(reports) && reports.length > 0
      ? applyOrbitReportUpdatesToTreeItems(mapped, reports)
      : mapped);
  };

  const commitSensors = (satId: string, nextSensors: SensorData[]) => {
    setSensorsBySat(prev => ({ ...prev, [satId]: nextSensors }));
    setCommittedSensorsBySat(prev => ({ ...prev, [satId]: nextSensors }));
    updateSatellite(satId, { sensors: nextSensors });
  };

  // When switching satellites: close sensor panel + snapshot committed state (first time only)
  useEffect(() => {
    if (!selectedItemId || analyserOpen || coverageAnalyserOpen) return;
    setPanelMinimized(false);
    setSensorPanelOpen(false);
    setEditingSensorIndex(null);
    setSensorPanelMode('add');
    setOrbitEditMode(selectedItemType === 'satellite' && selectedItemId === orbitalEditTargetId);
    setLiveOrbit(null);
    setFieldsBySat(prev => selectedItemId in prev ? prev : { ...prev, [selectedItemId]: {} });
    // Initialise sensors from MissionContext on first visit to this satellite
    setSensorsBySat(prev => {
      if (selectedItemId in prev) return prev;
      let sat: Satellite | null = null;
      for (const item of satellites) {
        if ((item as SatGroup).type === 'group') {
          const child = (item as SatGroup).children.find((c: Satellite) => c.id === selectedItemId);
          if (child) { sat = child; break; }
        } else if ((item as Satellite).id === selectedItemId) { sat = item as Satellite; break; }
      }
      if (!sat) return prev;
      return { ...prev, [selectedItemId]: sat.sensors ?? [] };
    });
    setCommittedSensorsBySat(prev => {
      if (selectedItemId in prev) return prev;
      let sat: Satellite | null = null;
      for (const item of satellites) {
        if ((item as SatGroup).type === 'group') {
          const child = (item as SatGroup).children.find((c: Satellite) => c.id === selectedItemId);
          if (child) { sat = child; break; }
        } else if ((item as Satellite).id === selectedItemId) { sat = item as Satellite; break; }
      }
      if (!sat) return prev;
      return { ...prev, [selectedItemId]: sat.sensors ?? [] };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId, selectedItemType, orbitalEditTargetId, analyserOpen, coverageAnalyserOpen, satellites]);

  useEffect(() => {
    if (analyserOpen || coverageAnalyserOpen) {
      setSensorPanelOpen(false);
      setEditingSensorIndex(null);
      setOrbitEditMode(false);
      cesium.clearSensorPreview();
      cesium.clearOrbitPreview();
    }
  }, [analyserOpen, coverageAnalyserOpen, cesium]);

  useEffect(() => {
    if (!selectedItemId || selectedItemType !== 'satellite' || orbitEditMode || analyserOpen || coverageAnalyserOpen) {
      setLiveOrbit(null);
      return;
    }

    let rafId = 0;
    let lastUpdateMs = 0;

    const updateLiveOrbit = (ts = performance.now()) => {
      if (ts - lastUpdateMs >= 100) {
        setLiveOrbit(getLiveSatelliteState(selectedItemId));
        lastUpdateMs = ts;
      }
      rafId = window.requestAnimationFrame(updateLiveOrbit);
    };

    setLiveOrbit(getLiveSatelliteState(selectedItemId));
    rafId = window.requestAnimationFrame(updateLiveOrbit);
    return () => window.cancelAnimationFrame(rafId);
  }, [getLiveSatelliteState, selectedItemId, selectedItemType, orbitEditMode, analyserOpen, coverageAnalyserOpen]);

  // Load global sensor clipboard from localStorage on mount
  useEffect(() => {
    const clipboardData = localStorage.getItem('sensorClipboard');
    if (clipboardData) {
      try {
        setSensorClipboard(JSON.parse(clipboardData));
      } catch (e) {
        console.error('Failed to parse sensor clipboard', e);
      }
    }
  }, []);

  // Sync sensor list to MissionContext whenever it changes so CesiumMap can draw FOVs
  useEffect(() => {
    if (!selectedItemId || selectedItemType !== 'satellite' || analyserOpen || coverageAnalyserOpen) return;
    if (sensorsBySat[selectedItemId] === undefined) return;
    const list = sensorsBySat[selectedItemId];
    updateSatellite(selectedItemId, { sensors: list });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sensorsBySat, selectedItemId, selectedItemType, analyserOpen, coverageAnalyserOpen]);

  const handleAddSensor = () => {
    if (shouldBlockUI) return;
    setSensorPanelMode('add');
    setEditingSensorIndex(null);
    setSensorPanelOpen(true);
    if (selectedItemId) {
      const previewSatId = selectedItemType === 'group'
        ? findGroupById(selectedItemId)?.children?.[0]?.id
        : selectedItemId;
      if (!previewSatId) return;
      cesium.flyToSensorPreview(previewSatId);
      cesium.showSensorPreview(previewSatId, {
        sensorId: '', name: '', geometry: 'conical', fov: '30', fovH: '60', fovV: '40',
        pointingMode: 'nadir', azimuth: '0', elevation: '0',
        yaw: '0', pitch: '0', roll: '0', dcmX: '0', dcmY: '0', dcmZ: '-1',
        lighting: [], agility: 30, bodyAxis: [0, 0, -1],
      });
    }
  };

  const handleEditSensor = (index: number) => {
    setSensorPanelMode('edit');
    setEditingSensorIndex(index);
    setSensorPanelOpen(true);
    if (selectedItemId) {
      if (selectedItemType === 'group') {
        const group = findGroupById(selectedItemId);
        const summary = group ? buildGroupSensorSummaries(group.children ?? [])[index] : null;
        const target = summary?.targets[0];
        if (target) {
          cesium.flyToSensorPreview(target.satId);
          cesium.showSensorPreview(target.satId, target.sensor);
        }
        return;
      }
      cesium.flyToSensorPreview(selectedItemId);
      const sensor = getSatelliteSensors(selectedItemId)[index];
      if (sensor) cesium.showSensorPreview(selectedItemId, sensor);
    }
  };

  const handleSensorPanelClose = () => {
    setSensorPanelOpen(false);
    cesium.clearSensorPreview();
    cesium.resetZoom();
  };

  const handleSensorPreviewChange = (partial: Partial<SensorData>) => {
    if (!selectedItemId) return;
    const group = selectedItemType === 'group' ? findGroupById(selectedItemId) : null;
    const previewSatId = group?.children?.[0]?.id ?? selectedItemId;
    const currentList = selectedItemType === 'group'
      ? [buildGroupSensorSummaries(group?.children ?? [])[editingSensorIndex ?? 0]?.targets[0]?.sensor].filter(Boolean) as SensorData[]
      : getSatelliteSensors(selectedItemId);
    const base: SensorData = editingSensorIndex !== null
      ? (selectedItemType === 'group' ? currentList[0] : currentList[editingSensorIndex])
      : { geometry: 'conical', fov: '30', fovH: '60', fovV: '40', pointingMode: 'nadir', azimuth: '0', elevation: '0', yaw: '0', pitch: '0', roll: '0', dcmX: '0', dcmY: '0', dcmZ: '-1', lighting: [] };
    cesium.showSensorPreview(previewSatId, { ...base, ...partial });
  };

  const appendSensorBulk = async (satId: string, incomingSensors: SensorData[]) => {
    if (incomingSensors.length === 0) return;
    const { missionId, scenarioId, groupId } = getSensorApiContext(satId);
    const current = getSatelliteSensors(satId);
    const sensorsWithIds = incomingSensors.map(withFreshSensorId);
    const res = await MissionService.addSatelliteSensorsBulk(missionId, scenarioId, groupId, satId, {
      sensors: sensorsWithIds.map(sensor => mapSensorToApi(sensor, true)),
    });
    const raw = res?.data?.data ?? [];
    const saved = Array.isArray(raw)
      ? raw.map((sensor, index) => mapApiSensorToLocal(sensor, sensorsWithIds[index]))
      : sensorsWithIds;
    commitSensors(satId, [...current, ...saved]);
  };

  const handlePasteSensor = () => {
    if (!selectedItemId || !sensorClipboard || sensorClipboard.length === 0) return;
    if (selectedItemType === 'group') {
      const group = findGroupById(selectedItemId);
      if (!group) return;
      Promise.all(group.children.map(child => appendSensorBulk(child.id, sensorClipboard)))
        .catch(e => console.error('[Group] Sensor bulk append failed', e));
      return;
    }
    appendSensorBulk(selectedItemId, sensorClipboard).catch(e => console.error('[Satellite] Sensor bulk append failed', e));
  };

  const handleSaveSensor = async (data: SensorData, editIndex?: number) => {
    if (!selectedItemId) return;
    invalidateAccessWindows();
    try {
      if (selectedItemType === 'group') {
        const group = findGroupById(selectedItemId);
        if (!group) return;

        if (sensorPanelMode === 'edit' && editingSensorIndex !== null) {
          const summary = buildGroupSensorSummaries(group.children ?? [])[editingSensorIndex];
          if (!summary) return;
          await Promise.all(group.children.map(async child => {
            const target = getSensorForGroupChild(summary, child.id);
            if (!target) {
              await appendSensorBulk(child.id, [data]);
              return;
            }

            const current = getSatelliteSensors(target.satId);
            const existing = current[target.sensorIndex] ?? target.sensor;
            const nextSensor = { ...data, sensorId: existing.sensorId };
            let saved: SensorData = nextSensor;

            if (mission_id && scenario_id && child.simulationGroupId) {
              if (existing.sensorId) {
                const res = await MissionService.updateSatelliteSensor(
                  mission_id,
                  scenario_id,
                  child.simulationGroupId,
                  target.satId,
                  existing.sensorId,
                  mapSensorToApi(nextSensor, true),
                );
                saved = mapApiSensorToLocal(res?.data?.data ?? {}, nextSensor);
              } else {
                const res = await MissionService.addSatelliteSensor(
                  mission_id,
                  scenario_id,
                  child.simulationGroupId,
                  target.satId,
                  mapSensorToApi(data),
                );
                saved = mapApiSensorToLocal(res?.data?.data ?? {}, data);
              }
            }

            const nextSensors = [...current];
            nextSensors[target.sensorIndex] = saved;
            commitSensors(target.satId, nextSensors);
          }));
          return;
        }

        const sensorsToAdd = sensorPanelMode === 'paste' && sensorClipboard
          ? [sensorClipboard[editIndex ?? 0]]
          : [data];
        await Promise.all(group.children.map(child => appendSensorBulk(child.id, sensorsToAdd)));
        return;
      }

      const { missionId, scenarioId, groupId } = getSensorApiContext(selectedItemId);
      const current = getSatelliteSensors(selectedItemId);

      if (sensorPanelMode === 'edit' && editingSensorIndex !== null) {
        const existing = current[editingSensorIndex];
        if (!existing?.sensorId) {
          const res = await MissionService.addSatelliteSensor(missionId, scenarioId, groupId, selectedItemId, mapSensorToApi(data));
          const saved = mapApiSensorToLocal(res?.data?.data ?? {}, data);
          const nextSensors = [...current];
          nextSensors[editingSensorIndex] = saved;
          commitSensors(selectedItemId, nextSensors);
          return;
        }
        const editBody = { ...mapSensorToApi(data), sensorId: existing.sensorId };
        const res = await MissionService.updateSatelliteSensor(missionId, scenarioId, groupId, selectedItemId, existing.sensorId, editBody);
        const saved = mapApiSensorToLocal(res?.data?.data ?? {}, { ...data, sensorId: existing.sensorId });
        const nextSensors = [...current];
        nextSensors[editingSensorIndex] = saved;
        commitSensors(selectedItemId, nextSensors);
        return;
      }

      if (sensorPanelMode === 'paste' && sensorClipboard) {
        await appendSensorBulk(selectedItemId, [sensorClipboard[editIndex ?? 0]]);
        return;
      }

      const res = await MissionService.addSatelliteSensor(missionId, scenarioId, groupId, selectedItemId, mapSensorToApi(data));
      const saved = mapApiSensorToLocal(res?.data?.data ?? {}, data);
      commitSensors(selectedItemId, [...current, saved]);
    } catch (e) {
      console.error('[Satellite] Sensor save failed', e);
    }
  };

  const handleRemoveSensor = async (index: number) => {
    if (!selectedItemId) return;
    invalidateAccessWindows();
    try {
      if (selectedItemType === 'group') {
        const group = findGroupById(selectedItemId);
        const summary = group ? buildGroupSensorSummaries(group.children ?? [])[index] : null;
        if (!group || !summary) return;
        await Promise.all(summary.targets.map(async target => {
          const child = group.children.find(item => item.id === target.satId);
          const current = getSatelliteSensors(target.satId);
          const existing = current[target.sensorIndex] ?? target.sensor;
          if (existing?.sensorId && mission_id && scenario_id && child?.simulationGroupId) {
            await MissionService.deleteSatelliteSensor(mission_id, scenario_id, child.simulationGroupId, target.satId, existing.sensorId);
          }
          commitSensors(target.satId, current.filter((_, i) => i !== target.sensorIndex));
        }));
        return;
      }

      const current = getSatelliteSensors(selectedItemId);
      const target = current[index];
      if (target?.sensorId) {
        const { missionId, scenarioId, groupId } = getSensorApiContext(selectedItemId);
        await MissionService.deleteSatelliteSensor(missionId, scenarioId, groupId, selectedItemId, target.sensorId);
      }
      commitSensors(selectedItemId, current.filter((_, i) => i !== index));
    } catch (e) {
      console.error('[Satellite] Sensor delete failed', e);
    }
  };

  const handleCopySensor = (index: number | 'all') => {
    const group = selectedItemId && selectedItemType === 'group' ? findGroupById(selectedItemId) : null;
    const activeSensors = group ? buildGroupSensorDisplayList(buildGroupSensorSummaries(group.children ?? [])) : sensors;
    const sensorsToCopy = index === 'all' ? activeSensors : [activeSensors[index]];
    localStorage.setItem(`sensorClipboard`, JSON.stringify(sensorsToCopy));
    setSensorClipboard(sensorsToCopy);
  };

  // Keyboard shortcut: Ctrl+V or Cmd+V for paste
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        if (!sensorClipboard || sensorClipboard.length === 0 || !selectedItemId) return;
        e.preventDefault();
        appendSensorBulk(selectedItemId, sensorClipboard).catch(err => console.error('[Satellite] Sensor bulk append failed', err));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [sensorClipboard, selectedItemId, sensorsBySat]); // eslint-disable-line react-hooks/exhaustive-deps

  // Live orbit preview — fires whenever an orbital field changes while editing
  useEffect(() => {
    if (!selectedItemId || selectedItemType !== 'satellite') {
      cesium.clearOrbitPreview();
      return;
    }
    const fields = fieldsBySat[selectedItemId] ?? {};
    if (Object.keys(fields).length === 0) {
      cesium.clearOrbitPreview();
      return;
    }
    // Find committed satellite to fill unedited fields
    let sat: Satellite | null = null;
    for (const item of satellites) {
      if ((item as SatGroup).type === 'group') {
        const child = (item as SatGroup).children.find((c: Satellite) => c.id === selectedItemId);
        if (child) { sat = child; break; }
      } else if ((item as Satellite).id === selectedItemId) { sat = item as Satellite; break; }
    }
    const orb = sat?.orbitElements;
    if (!orb) { cesium.clearOrbitPreview(); return; }

    const orbitType = meta?.orbitType ?? 'inclined';
    const liveEpochStr = 'epoch' in fields ? fields.epoch : epochDisplay;

    let liveRaan = orb.RAAN;
    if (orbitType === 'sso') {
      const liveLtan = 'ltan' in fields ? fields.ltan : (meta?.ltan ?? '10:30:00');
      liveRaan = ltanToRaan(liveLtan, liveEpochStr, 'UTC');
    } else if ('raan' in fields) {
      liveRaan = parseFloat(fields.raan) || 0;
    }

    const liveOrb = {
      semiMajorAxis: 'altitude' in fields ? (parseFloat(fields.altitude) || 0) + 6371 : orb.semiMajorAxis,
      eccentricity: orbitType === 'sso' || orbitType === 'geo' ? 0 : ('eccentricity' in fields ? parseFloat(fields.eccentricity) || 0 : orb.eccentricity),
      inclination: 'inclination' in fields ? parseFloat(fields.inclination) || 0 : orb.inclination,
      RAAN: liveRaan,
      AOP: orbitType === 'sso' || orbitType === 'geo' ? 0 : ('aop' in fields ? parseFloat(fields.aop) || 0 : orb.AOP),
      TA: orbitType === 'sso' ? 0 : (orbitType === 'geo' ? ('longitude' in fields ? parseFloat(fields.longitude) || 0 : orb.TA) : ('ta' in fields ? parseFloat(fields.ta) || 0 : orb.TA)),
      epoch: 'epoch' in fields ? parseEpochDisplay(fields.epoch) : orb.epoch,
    };
    if (liveOrb.semiMajorAxis > 0) {
      cesium.showOrbitPreview([liveOrb]);
      if (liveOrb.epoch > 0) {
        cesium.setClockTime(new Date(liveOrb.epoch * 1000));
      }
    } else {
      cesium.clearOrbitPreview();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsBySat, selectedItemId]);

  // Clear preview when satellite selection changes or panel unmounts
  useEffect(() => {
    return () => { cesium.clearOrbitPreview(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItemId]);

  // ── Edit field helpers ──────────────────────────────────────────────────────
  const currentFields = selectedItemId ? (fieldsBySat[selectedItemId] ?? {}) : {};

  const getField = (key: string, original: string) => currentFields[key] ?? original;

  const setField = (key: string, value: string) => {
    if (!selectedItemId) return;
    if (ORBIT_FIELD_KEYS.has(key)) setOrbitEditMode(true);
    setFieldsBySat(prev => ({
      ...prev,
      [selectedItemId]: { ...(prev[selectedItemId] ?? {}), [key]: value },
    }));
  };

  // Dirty detection
  const fieldsDirty = Object.keys(currentFields).length > 0;
  const isDirty = fieldsDirty;

  // ── Update / Cancel ─────────────────────────────────────────────────────────
  const handleUpdate = async () => {
    if (!selectedItemId) return;
    const fields = fieldsBySat[selectedItemId] ?? {};

    if (selectedItemType === 'group') {
      const grp = satellites.find(
        (item): item is SatGroup => (item as SatGroup).type === 'group' && item.id === selectedItemId
      );
      if (!grp) return;

      const orbitColour = fields.orbitColour;

      // 1. Save orbit color if it was edited (when not in groupEditMode)
      if (orbitColour && !orbitalEditTargetId) {
        try {
          if (!mission_id || !scenario_id) throw new Error('Missing mission or scenario id');
          setSaving(true);
          const refSat = grp.children?.[0];
          const prevPhys = refSat?.physicalCharacteristics;
          const physicalCharacteristics = {
            mass: prevPhys?.mass ?? parseFloat(DEFAULT_PHYS.mass),
            volume: prevPhys?.volume ?? parseFloat(DEFAULT_PHYS.volume),
            cd: prevPhys?.cd ?? parseFloat(DEFAULT_PHYS.dragCoeff),
            dragArea: prevPhys?.dragArea ?? parseFloat(DEFAULT_PHYS.dragArea),
            cr: prevPhys?.cr ?? parseFloat(DEFAULT_PHYS.reflectCoeff),
            srpArea: prevPhys?.srpArea ?? parseFloat(DEFAULT_PHYS.srpArea),
            inertia: prevPhys?.inertia ?? { Ixx: 0.015, Iyy: 0.05, Izz: 0.06, Ixy: 0, Ixz: 0, Iyz: 0 },
            orientation: prevPhys?.orientation ?? { Roll: 0, Pitch: 0, Yaw: 0 },
          };
          const reqBody = {
            physicalCharacteristics,
            orbitColour,
            orbitColor: orbitColour,
          };
          await MissionService.updateGroupSatellitesConfig(mission_id, scenario_id, selectedItemId, reqBody);
          updateSatellite(selectedItemId, { orbitColour });
        } catch (e) {
          console.error('[Group] Orbit color update failed', e);
          setSaving(false);
          return;
        }
      }

      const groupEditMode = orbitalEditTargetId === selectedItemId;

      if (groupEditMode) {
        const referenceSat = grp.children[0];
        const orb = referenceSat?.orbitElements;
        const meta = referenceSat?.orbitMeta;
        if (!referenceSat || !orb) return;

        const altitude = meta?.altitude_km != null
          ? String(meta.altitude_km)
          : (orb.semiMajorAxis - 6371).toFixed(1);
        const inclination = meta?.inclination_deg != null ? String(meta.inclination_deg) : orb.inclination.toFixed(4);
        const raan = meta?.raan_deg != null ? String(meta.raan_deg) : orb.RAAN.toFixed(4);
        const aop = meta?.aop_deg != null ? String(meta.aop_deg) : orb.AOP.toFixed(4);
        const ta = meta?.trueAnomaly_deg != null ? String(meta.trueAnomaly_deg) : orb.TA.toFixed(4);
        const eccentricity = meta?.eccentricity != null ? String(meta.eccentricity) : orb.eccentricity.toFixed(6);
        const epochDisplay = meta?.epoch?.replace(' UTC', '') ?? new Date(orb.epoch * 1000).toISOString().slice(0, 19).replace('T', ' ');
        const noOfSatellites = Math.max(1, Math.round(Number(getField('groupNumSats', String(grp.children.length))) || grp.children.length));
        const nextAltitude = parseFloat(getField('altitude', altitude)) || 0;
        const nextEccentricity = parseFloat(getField('eccentricity', eccentricity)) || 0;
        const nextEpoch = parseEpochDisplay(getField('epoch', epochDisplay));
        const checkSma = nextAltitude + 6371;
        const checkRp = checkSma * (1 - nextEccentricity);
        const checkPeriapsisAltKm = checkRp - 6371;
        if (checkPeriapsisAltKm < 100) {
          showToastMessage('error', "Invalid orbital configuration. The orbit intersects the Earth's surface. Please modify the orbital parameters.");
          return;
        }
        const nextInterPlanePhasing = grp.children.length > 1
          ? Math.abs((grp.children[1].orbitElements?.TA ?? 0) - (grp.children[0].orbitElements?.TA ?? 0))
          : 0;

        const body = {
          type: 'Group',
          name: grp.name,
          defaultNaming: false,
          orbitType: meta?.orbitType ?? 'inclined',
          groupProperties: {
            type: 'Single Plane',
            noOfSatellites,
            interAnglePhasingForSinglePlane: nextInterPlanePhasing,
          },
          referenceOrbit: {
            epoch: nextEpoch,
            semiMajorAxis: nextAltitude + 6371,
            eccentricity: parseFloat(getField('eccentricity', eccentricity)) || 0,
            inclination: parseFloat(getField('inclination', inclination)) || 0,
            RAAN: parseFloat(getField('raan', raan)) || 0,
            AOP: parseFloat(getField('aop', aop)) || 0,
            TA: parseFloat(getField('ta', ta)) || 0,
            altitude: nextAltitude,
          },
        };

        try {
          setSaving(true);
          if (!mission_id || !scenario_id) throw new Error('Missing mission or scenario id');
          await MissionService.editSatelliteGroup(mission_id, scenario_id, selectedItemId, body);
          await refreshSatellites();

          // 2. Save orbit color if it was edited (when in groupEditMode)
          if (orbitColour) {
            const refSat = grp.children?.[0];
            const prevPhys = refSat?.physicalCharacteristics;
            const physicalCharacteristics = {
              mass: prevPhys?.mass ?? parseFloat(DEFAULT_PHYS.mass),
              volume: prevPhys?.volume ?? parseFloat(DEFAULT_PHYS.volume),
              cd: prevPhys?.cd ?? parseFloat(DEFAULT_PHYS.dragCoeff),
              dragArea: prevPhys?.dragArea ?? parseFloat(DEFAULT_PHYS.dragArea),
              cr: prevPhys?.cr ?? parseFloat(DEFAULT_PHYS.reflectCoeff),
              srpArea: prevPhys?.srpArea ?? parseFloat(DEFAULT_PHYS.srpArea),
              inertia: prevPhys?.inertia ?? { Ixx: 0.015, Iyy: 0.05, Izz: 0.06, Ixy: 0, Ixz: 0, Iyz: 0 },
              orientation: prevPhys?.orientation ?? { Roll: 0, Pitch: 0, Yaw: 0 },
            };
            const reqBody = {
              physicalCharacteristics,
              orbitColour,
              orbitColor: orbitColour,
            };
            await MissionService.updateGroupSatellitesConfig(mission_id, scenario_id, selectedItemId, reqBody);
            updateSatellite(selectedItemId, { orbitColour });
          }
        } catch (e) {
          console.error('[Group] Update failed', e);
          setSaving(false);
          return;
        }

        setFieldsBySat(prev => ({ ...prev, [selectedItemId]: {} }));
        setOrbitEditMode(false);
        setSaving(false);
        cesium.clearOrbitPreview();
        return;
      }

      const summaries = buildGroupSensorSummaries(grp.children ?? []);
      const summaryMap = new Map(summaries.map(summary => [summary.id, summary]));
      const edits = Object.entries(fields)
        .map(([key, value]) => {
          const parsed = parseGroupSensorFieldKey(key);
          return parsed ? { ...parsed, value } : null;
        })
        .filter((edit): edit is { summaryId: string; field: keyof SensorData; value: string } => !!edit);

      if (edits.length === 0) {
        setFieldsBySat(prev => ({ ...prev, [selectedItemId]: {} }));
        setSaving(false);
        return;
      }

      const editsBySummary = edits.reduce((acc, edit) => {
        const existing = acc.get(edit.summaryId) ?? {};
        existing[edit.field] = edit.value;
        acc.set(edit.summaryId, existing);
        return acc;
      }, new Map<string, Partial<Record<keyof SensorData, string>>>());

      try {
        setSaving(true);
        const requests: Array<Promise<unknown>> = [];
        editsBySummary.forEach((patch, summaryId) => {
          const summary = summaryMap.get(summaryId);
          if (!summary) return;
          summary.targets.forEach(target => {
            const nextSensor = { ...target.sensor, ...patch } as SensorData;
            const child = grp.children.find(item => item.id === target.satId);
            if (!child) return;
            const nextSensors = [...(child.sensors ?? [])];
            nextSensors[target.sensorIndex] = nextSensor;
            updateSatellite(target.satId, { sensors: nextSensors });
            if (mission_id && scenario_id && child.simulationGroupId && nextSensor.sensorId) {
              requests.push(MissionService.updateSatelliteSensor(
                mission_id,
                scenario_id,
                child.simulationGroupId,
                target.satId,
                nextSensor.sensorId,
                mapSensorToApi(nextSensor, true),
              ));
            }
          });
        });
        if (requests.length) await Promise.all(requests);
      } catch (e) {
        console.error('[Group] Sensor update failed', e);
        setSaving(false);
        return;
      }

      setFieldsBySat(prev => ({ ...prev, [selectedItemId]: {} }));
      setSaving(false);
      return;
    }

    // Find selected satellite
    let sat: Satellite | null = null;
    for (const item of satellites) {
      if ((item as SatGroup).type === 'group') {
        const grp = item as SatGroup;
        const child = grp.children.find(c => c.id === selectedItemId);
        if (child) { sat = child; break; }
        if (grp.id === selectedItemId) { sat = grp.children?.[0] || null; break; }
      } else if ((item as Satellite).id === selectedItemId) { sat = item as Satellite; break; }
    }

    const hasOrbEdit = !sat?.tleMeta && ['altitude', 'inclination', 'raan', 'aop', 'ta', 'epoch',
      'ltan', 'longitude', 'perigee', 'apogee', 'eccentricity'].some(k => k in fields);

    // ── orbitalDetails ───────────────────────────────────────────────────────
    const origOrb = sat?.orbitElements;
    const updatedOrb = hasOrbEdit && origOrb ? {
      semiMajorAxis: 'altitude' in fields ? (parseFloat(fields.altitude) || 0) + 6371 : origOrb.semiMajorAxis,
      eccentricity: 'eccentricity' in fields ? parseFloat(fields.eccentricity) || 0 : origOrb.eccentricity,
      inclination: 'inclination' in fields ? parseFloat(fields.inclination) || 0 : origOrb.inclination,
      RAAN: 'raan' in fields ? parseFloat(fields.raan) || 0 : origOrb.RAAN,
      AOP: 'aop' in fields ? parseFloat(fields.aop) || 0 : origOrb.AOP,
      TA: 'ta' in fields ? parseFloat(fields.ta) || 0 : origOrb.TA,
      epoch: 'epoch' in fields ? parseEpochDisplay(fields.epoch) : origOrb.epoch,
    } : null;

    if (updatedOrb) {
      const rp = updatedOrb.semiMajorAxis * (1 - updatedOrb.eccentricity);
      const periapsisAltKm = rp - 6371;
      if (periapsisAltKm < 100) {
        showToastMessage('error', "Invalid orbital configuration. The orbit intersects the Earth's surface. Please modify the orbital parameters.");
        return;
      }
    }

    // ── physicalCharacteristics — only edited fields ──────────────────────────
    const hasPhysEdit = ['mass', 'volume', 'dragCoeff', 'dragArea', 'reflectCoeff', 'srpArea'].some(k => k in fields);
    const prevPhys = sat?.physicalCharacteristics;
    const physicalCharacteristics = {
      mass: 'mass' in fields ? parseFloat(fields.mass) || 0 : prevPhys?.mass ?? parseFloat(DEFAULT_PHYS.mass),
      volume: 'volume' in fields ? parseFloat(fields.volume) || 0 : prevPhys?.volume ?? parseFloat(DEFAULT_PHYS.volume),
      cd: 'dragCoeff' in fields ? parseFloat(fields.dragCoeff) || 0 : prevPhys?.cd ?? parseFloat(DEFAULT_PHYS.dragCoeff),
      dragArea: 'dragArea' in fields ? parseFloat(fields.dragArea) || 0 : prevPhys?.dragArea ?? parseFloat(DEFAULT_PHYS.dragArea),
      cr: 'reflectCoeff' in fields ? parseFloat(fields.reflectCoeff) || 0 : prevPhys?.cr ?? parseFloat(DEFAULT_PHYS.reflectCoeff),
      srpArea: 'srpArea' in fields ? parseFloat(fields.srpArea) || 0 : prevPhys?.srpArea ?? parseFloat(DEFAULT_PHYS.srpArea),
      inertia: prevPhys?.inertia ?? { Ixx: 0.015, Iyy: 0.05, Izz: 0.06, Ixy: 0, Ixz: 0, Iyz: 0 },
      orientation: prevPhys?.orientation ?? { Roll: 0, Pitch: 0, Yaw: 0 },
    };

    // ── Build final request body ─────────────────────────────────────────────
    const orbitColour = fields.orbitColour || sat?.orbitColour || MODELLING_DEFAULT_ORBIT_COLOR;
    const reqBody = {
      physicalCharacteristics,
      orbitColour,
      orbitColor: orbitColour,
    };

    try {
      if (Object.keys(reqBody).length > 0) {
        if (!mission_id || !scenario_id) throw new Error('Missing mission or scenario id');
        if (!sat?.simulationGroupId) throw new Error('Missing satellite group id');
        setSaving(true);
        await MissionService.updateSingleSatelliteConfig(mission_id, scenario_id, sat.simulationGroupId, selectedItemId, reqBody);
      }
    } catch (e) {
      console.error('[Satellite] Update failed', e);
      setSaving(false);
      return;
    }

    if (updatedOrb || physicalCharacteristics || orbitColour) {
      invalidateAccessWindows();
    }

    if (updatedOrb) updateSatellite(selectedItemId, { orbitElements: updatedOrb });
    if (physicalCharacteristics) {
      updateSatellite(selectedItemId, { physicalCharacteristics });
    }

    if (orbitColour) {
      updateSatellite(selectedItemId, { orbitColour });
    }

    // Clear field edits and orbit preview
    setFieldsBySat(prev => ({ ...prev, [selectedItemId]: {} }));
    setOrbitEditMode(false);
    setSaving(false);
    cesium.clearOrbitPreview();
  };

  const handleCancel = () => {
    if (!selectedItemId) return;
    // Revert field edits and orbit preview
    setFieldsBySat(prev => ({ ...prev, [selectedItemId]: {} }));
    setOrbitEditMode(false);
    cesium.clearOrbitPreview();
    // Revert sensors to committed snapshot
    setSensorsBySat(prev => ({ ...prev, [selectedItemId]: committedSensorsBySat[selectedItemId] ?? [] }));
  };

  const handleClosePanel = useCallback(() => {
    if (saving) return;
    if (isDirty || sensorPanelOpen) {
      const shouldClose = window.confirm('Discard unsaved satellite changes?');
      if (!shouldClose) return;
    }
    if (selectedItemId) {
      setFieldsBySat(prev => ({ ...prev, [selectedItemId]: {} }));
      setSensorsBySat(prev => ({ ...prev, [selectedItemId]: committedSensorsBySat[selectedItemId] ?? [] }));
    }
    setSensorPanelOpen(false);
    setEditingSensorIndex(null);
    setSensorPanelMode('add');
    setOrbitEditMode(false);
    cesium.clearOrbitPreview();
    cesium.clearSensorPreview();
    selectTreeItem(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [committedSensorsBySat, isDirty, saving, selectTreeItem, selectedItemId, sensorPanelOpen]);

  const handleToggleMinimize = useCallback(() => {
    setPanelMinimized((nextMinimized) => {
      const minimized = !nextMinimized;
      if (minimized) {
        setSensorPanelOpen(false);
        cesium.clearSensorPreview();
      }
      return minimized;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedItemId || (selectedItemType !== 'satellite' && selectedItemType !== 'group')) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      handleClosePanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleClosePanel, selectedItemId, selectedItemType]);

  if (!selectedItemId || (selectedItemType !== 'satellite' && selectedItemType !== 'group')) return null;
  if (analyserOpen || coverageAnalyserOpen) return null;

  // ── Group panel ────────────────────────────────────────────────────────────
  if (selectedItemType === 'group') {
    const grp = satellites.find(
      (it): it is SatGroup => (it as SatGroup).type === 'group' && it.id === selectedItemId
    );
    if (!grp) return null;

    const children: Satellite[] = grp.children ?? [];
    const groupEditMode = orbitalEditTargetId === selectedItemId;
    const referenceSat = children[0] ?? null;
    const groupOrb = referenceSat?.orbitElements;
    const groupMeta = referenceSat?.orbitMeta;
    const groupOrbitTypeLabel = groupMeta?.orbitTypeLabel ?? 'Keplerian';
    const groupAltitude = groupMeta?.altitude_km != null
      ? String(groupMeta.altitude_km)
      : groupOrb ? (groupOrb.semiMajorAxis - 6371).toFixed(1) : '—';
    const groupInclination = groupMeta?.inclination_deg != null ? String(groupMeta.inclination_deg) : groupOrb ? groupOrb.inclination.toFixed(4) : '—';
    const groupRaan = groupMeta?.raan_deg != null ? String(groupMeta.raan_deg) : groupOrb ? groupOrb.RAAN.toFixed(4) : '—';
    const groupAop = groupMeta?.aop_deg != null ? String(groupMeta.aop_deg) : groupOrb ? groupOrb.AOP.toFixed(4) : '—';
    const groupTa = groupMeta?.trueAnomaly_deg != null ? String(groupMeta.trueAnomaly_deg) : groupOrb ? groupOrb.TA.toFixed(4) : '—';
    const groupEccentricity = groupMeta?.eccentricity != null ? String(groupMeta.eccentricity) : groupOrb ? groupOrb.eccentricity.toFixed(6) : '—';
    const groupEpochDisplay = groupMeta?.epoch?.replace(' UTC', '') ?? (groupOrb ? new Date(groupOrb.epoch * 1000).toISOString().slice(0, 19).replace('T', ' ') : '—');
    const groupSensorSummaries = buildGroupSensorSummaries(children);
    const groupSensors = buildGroupSensorDisplayList(groupSensorSummaries);
    const groupSensorCoverage = groupSensorSummaries.map(summary => summary.targets.length);

    return (
      <>
        <div className={`${styles.panel} ${groupEditMode ? styles.groupEditPanel : ''} ${panelMinimized ? styles.panelMinimized : ''}`} style={propertyPanelStyle}>
          <div className={styles.header} {...dragHeaderProps}>
            <span className={styles.kindBadge}>{groupEditMode ? 'EDIT GROUP' : 'GROUP'}</span>
            <span className={styles.satName}>{grp.name}</span>
            <div className={styles.headerActions}>
              <CustomToolTip title={panelMinimized ? 'Restore' : 'Minimize'}>
                <button className={styles.headerBtn} type="button" onClick={handleToggleMinimize}>
                  <ToggleMinimizeSvg type={panelMinimized ? 'maximize' : 'minimize'} />
                </button>
              </CustomToolTip>
              <CustomToolTip title="Close">
                <button className={styles.headerBtn} type="button" onClick={handleClosePanel}>
                  <CloseSvg />
                </button>
              </CustomToolTip>
            </div>
          </div>

          {!panelMinimized && (
            <div className={styles.panelBody}>
              {groupEditMode && (
                <div className={`${styles.form} ${styles.propertyGroupEditForm}`}>
                  <div className={styles.fieldStack}>
                    <span className={styles.fieldLabel}>Group Name</span>
                    <div className={`${styles.fieldInput} ${styles.fieldDisabled}`}>
                      <input value={grp.name} disabled />
                    </div>
                  </div>

                  <div className={styles.fsSection}>
                    <div className={styles.fsSectionHd}>
                      <span>Reference Orbit</span>
                      <SmallChevronIcon />
                    </div>
                    <div className={styles.fsSectionBody}>
                      <div className={styles.fieldStack}>
                        <span className={styles.fieldLabel}>Orbit</span>
                        <div className={`${styles.selectInput} ${styles.fieldDisabled}`}>
                          <span>{groupOrbitTypeLabel}</span>
                          <SmallChevronIcon />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={styles.fsSection}>
                    <div className={styles.fsSectionHd}>
                      <span>Orbital Details</span>
                      <em>/</em>
                    </div>
                    <div className={styles.fsSectionBody}>
                      <div className={`${styles.fieldStack} ${styles.epochFieldStack}`}>
                        <span className={styles.fieldLabel}>Epoch</span>
                        <div className={styles.fieldInput}>
                          <input
                            type="datetime-local"
                            value={formatEpochInput(getField('epoch', groupEpochDisplay))}
                            onChange={e => setField('epoch', formatEpochDisplayInput(e.target.value))}
                            disabled={shouldBlockUI}
                          />
                          <span className={styles.unitToggle}>UTC</span>
                        </div>
                      </div>

                      <div className={styles.fieldStack}>
                        <div className={styles.fieldLabelRow}>
                          <span className={styles.fieldLabel}>Altitude</span>
                          <span className={styles.fieldSuffixChip}>SMA</span>
                        </div>
                        <div className={styles.fieldRow2}>
                          <div className={styles.numericInput}>
                            <input type="number" value={getField('altitude', groupAltitude)} onChange={e => setField('altitude', e.target.value)} disabled={shouldBlockUI} />
                            <span className={styles.numericSuffix}>km</span>
                          </div>
                          <span className={styles.fieldSuffixChip}>MSL</span>
                        </div>
                      </div>

                      <div className={styles.fieldStack}>
                        <span className={styles.fieldLabel}>Eccentricity (e)</span>
                        <div className={styles.fieldInput}>
                          <input type="number" value={getField('eccentricity', groupEccentricity)} onChange={e => setField('eccentricity', e.target.value)} disabled={shouldBlockUI} />
                        </div>
                      </div>

                      {[
                        ['Inclination (i)', 'inclination', groupInclination],
                        ['RAAN (Ω)', 'raan', groupRaan],
                        ['AOP (ω)', 'aop', groupAop],
                        ['TA (θ)', 'ta', groupTa],
                      ].map(([label, key, original]) => (
                        <div className={styles.fieldStack} key={key}>
                          <span className={styles.fieldLabel}>{label}</span>
                          <div className={styles.numericInput}>
                            <input type="number" value={getField(key, original)} onChange={e => setField(key, e.target.value)} disabled={shouldBlockUI} />
                            <span className={styles.numericSuffix}>°</span>
                          </div>
                        </div>
                      ))}

                      <div className={`${styles.fieldStack} ${styles.referenceFrameStack}`}>
                        <span className={styles.fieldLabel}>Reference Frame</span>
                        <div className={`${styles.selectInput} ${styles.fieldDisabled}`}>
                          <span>{groupMeta?.refFrame ?? 'EME2000'}</span>
                          <SmallChevronIcon />
                        </div>
                      </div>

                      {SHOW_ORBIT_COLOR_PICKER && (
                        <div className={styles.colorRow}>
                          <span className={styles.colorLabel}>Orbit Color</span>
                          <div className={styles.colorPickerGrid}>
                            {MODELLING_ORBIT_COLORS.map(color => {
                              const colorArray = color.replace("[", "").replace("]", "").split(",").map(Number);
                              const rgbString = `rgba(${colorArray[0]},${colorArray[1]},${colorArray[2]})`;
                              const currentSelectedColor = getField('orbitColour', grp.orbitColour || MODELLING_DEFAULT_ORBIT_COLOR);
                              return (
                                <button
                                  key={color}
                                  type="button"
                                  onClick={() => setField('orbitColour', color)}
                                  className={`${styles.colorPickerButton} ${currentSelectedColor === color ? styles.colorSelected : ''}`}
                                  style={{ backgroundColor: rgbString }}
                                  disabled={shouldBlockUI}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className={styles.fsSection}>
                    <div className={styles.fsSectionHd}>
                      <span>Group Configurations</span>
                      <SmallChevronIcon />
                    </div>
                    <div className={styles.fsSectionBody}>
                      <div className={styles.fieldStack}>
                        <span className={styles.fieldLabel}>Group Type</span>
                        <div className={`${styles.selectInput} ${styles.fieldDisabled}`}>
                          <span>Single Plane</span>
                          <SmallChevronIcon />
                        </div>
                      </div>
                      <div className={styles.fieldRow2cfg}>
                        <span className={styles.cfgLabel}>Number of Satellites</span>
                        <div className={styles.numericInput}>
                          <input type="number" value={getField('groupNumSats', String(children.length))} onChange={e => setField('groupNumSats', e.target.value)} disabled={shouldBlockUI} />
                        </div>
                      </div>
                      <div className={styles.fieldRow2cfg}>
                        <span className={styles.cfgLabel}>Inter Plane Phasing</span>
                        <div className={styles.fieldWithIcon}>
                          <button className={`${styles.evenBtn} ${styles.evenBtnActive}`} type="button" disabled>↔</button>
                          <div className={`${styles.numericInput} ${styles.fieldDisabled}`}>
                            <input type="number" value="120" disabled />
                            <span className={styles.numericSuffix}>°</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!groupEditMode && SHOW_ORBIT_COLOR_PICKER && (
                <CollapsibleSection title="Group Configuration" defaultOpen>
                  <div className={styles.colorRow}>
                    <span className={styles.colorLabel}>Orbit Color</span>
                    <div className={styles.colorPickerGrid}>
                      {MODELLING_ORBIT_COLORS.map(color => {
                        const colorArray = color.replace("[", "").replace("]", "").split(",").map(Number);
                        const rgbString = `rgba(${colorArray[0]},${colorArray[1]},${colorArray[2]})`;
                        const currentSelectedColor = getField('orbitColour', grp.orbitColour || MODELLING_DEFAULT_ORBIT_COLOR);
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setField('orbitColour', color)}
                            className={`${styles.colorPickerButton} ${currentSelectedColor === color ? styles.colorSelected : ''}`}
                            style={{ backgroundColor: rgbString }}
                            disabled={shouldBlockUI}
                          />
                        );
                      })}
                    </div>
                  </div>
                </CollapsibleSection>
              )}

              {!groupEditMode && (
                <CollapsibleSection title={`Satellites (${children.length})`} defaultOpen>
                  {children.map(child => (
                    <div key={child.id} className={styles.row} style={{ cursor: 'pointer' }} onClick={() => selectTreeItem(child.id, 'satellite')}>
                      <CustomToolTip title={child.name} placement="top-start" className={styles.rowLabel}>
                        {child.name}
                      </CustomToolTip>
                      <span className={styles.staticValue} style={{ fontSize: '10px', opacity: 0.55 }}>
                        {child.orbitMeta?.orbitTypeLabel ?? 'Keplerian'}
                      </span>
                    </div>
                  ))}
                </CollapsibleSection>
              )}

              {!groupEditMode && (
                <CollapsibleSection title="Subsystems & Sensors" defaultOpen>
                  <BusSection
                    onAddSensor={handleAddSensor}
                    onEditSensor={handleEditSensor}
                    onPasteSensor={handlePasteSensor}
                    sensors={groupSensors}
                    groupSatelliteCount={children.length}
                    sensorCoverage={groupSensorCoverage}
                    onRemoveSensor={handleRemoveSensor}
                    onCopySensor={handleCopySensor}
                    sensorClipboard={sensorClipboard}
                    disabled={shouldBlockUI}
                  />
                </CollapsibleSection>
              )}

              {isDirty && (
                <div className={styles.actionFooter}>
                  <button className={styles.cancelActionBtn} onClick={handleCancel} type="button" disabled={saving}>Cancel</button>
                  <button className={styles.updateBtn} onClick={handleUpdate} type="button" disabled={saving}>
                    {saving ? 'Updating...' : 'Update'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {!panelMinimized && !groupEditMode && sensorPanelOpen && (
          <SensorDetailsPanel
            onSave={handleSaveSensor}
            onClose={handleSensorPanelClose}
            mode={sensorPanelMode}
            initialData={editingSensorIndex !== null ? groupSensorSummaries[editingSensorIndex]?.targets[0]?.sensor : undefined}
            pasteOptions={sensorClipboard || []}
            onPreviewChange={handleSensorPreviewChange}
            saveLabel={sensorPanelMode === 'edit' ? 'Apply to Group' : undefined}
            style={sensorPanelStyle}
            existingNames={groupSensors.map(s => s.name?.trim().toLowerCase()).filter((n): n is string => !!n)}
            existingSensors={groupSensors}
          />
        )}
      </>
    );
  }

  // Find the selected item from satellites tree
  let selectedName = selectedItemId;
  let selectedSat: Satellite | null = null;
  let isGroupChild = false;
  for (const item of satellites) {
    if ((item as SatGroup).type === 'group') {
      const grp = item as SatGroup;
      const child = grp.children.find((c: Satellite) => c.id === selectedItemId);
      if (child) {
        selectedName = child.name;
        selectedSat = child;
        isGroupChild = true;
        break;
      }
      if (grp.id === selectedItemId) {
        selectedName = grp.name;
        selectedSat = grp.children?.[0] || null;
        break;
      }
    } else if ((item as Satellite).id === selectedItemId) {
      selectedSat = item as Satellite;
      selectedName = selectedSat.name;
      break;
    }
  }

  const orb = selectedSat?.orbitElements;
  const meta = selectedSat?.orbitMeta;
  const tleMeta = selectedSat?.tleMeta;
  const isTleSat = !!tleMeta;
  const EARTH_RADIUS = 6371;
  const phys = selectedSat?.physicalCharacteristics;
  const physDisplay = {
    mass: phys?.mass != null ? String(phys.mass) : DEFAULT_PHYS.mass,
    volume: phys?.volume != null ? String(phys.volume) : DEFAULT_PHYS.volume,
    dragCoeff: phys?.cd != null ? String(phys.cd) : DEFAULT_PHYS.dragCoeff,
    dragArea: phys?.dragArea != null ? String(phys.dragArea) : DEFAULT_PHYS.dragArea,
    reflectCoeff: phys?.cr != null ? String(phys.cr) : DEFAULT_PHYS.reflectCoeff,
    srpArea: phys?.srpArea != null ? String(phys.srpArea) : DEFAULT_PHYS.srpArea,
  };

  // Prefer orbitMeta values (original input units) over back-calculated Keplerian
  const altitude = meta ? String(meta.altitude_km) : orb ? (orb.semiMajorAxis - EARTH_RADIUS).toFixed(1) : '—';
  const inclination = meta ? String(meta.inclination_deg) : orb ? orb.inclination.toFixed(4) : '—';
  const raan = meta ? String(meta.raan_deg) : orb ? orb.RAAN.toFixed(4) : '—';
  const aop = meta ? String(meta.aop_deg) : orb ? orb.AOP.toFixed(4) : '—';
  const ta = meta ? String(meta.trueAnomaly_deg) : orb ? orb.TA.toFixed(4) : '—';
  const epochDisplay = meta?.epoch?.replace(' UTC', '') ?? (orb ? new Date(orb.epoch * 1000).toISOString().slice(0, 19).replace('T', ' ') : '—');

  const activeLiveOrbit = liveOrbit ?? (selectedItemId && selectedItemType === 'satellite' ? getLiveSatelliteState(selectedItemId) : null);
  const liveSemiMajorAxis = activeLiveOrbit?.semiMajorAxis ?? (orb ? orb.semiMajorAxis : null);
  const period = liveSemiMajorAxis
    ? ((2 * Math.PI * Math.sqrt(Math.pow(liveSemiMajorAxis, 3) / 398600.4418)) / 60).toFixed(1)
    : '—';

  const liveMeanAnomaly = activeLiveOrbit?.meanAnomaly != null
    ? activeLiveOrbit.meanAnomaly.toFixed(2)
    : (orb && activeLiveOrbit
      ? (((((360 / (Number(period) * 60)) * (activeLiveOrbit.epochSec - orb.epoch)) + orb.TA) % 360 + 360) % 360).toFixed(2)
      : '—');

  const liveMode = !isTleSat && !orbitEditMode;
  const live = {
    latitude: activeLiveOrbit ? activeLiveOrbit.latitudeDeg.toFixed(3) : '—',
    longitude: activeLiveOrbit ? activeLiveOrbit.longitudeDeg.toFixed(3) : '—',
    altitude: activeLiveOrbit ? activeLiveOrbit.altitudeKm.toFixed(1) : altitude,
    velocity: activeLiveOrbit?.velocityKmS != null ? activeLiveOrbit.velocityKmS.toFixed(3) : '—',
    semiMajorAxis: activeLiveOrbit?.semiMajorAxis != null ? activeLiveOrbit.semiMajorAxis.toFixed(1) : (orb ? orb.semiMajorAxis.toFixed(1) : '—'),
    eccentricity: activeLiveOrbit?.eccentricity != null ? activeLiveOrbit.eccentricity.toFixed(4) : (orb ? orb.eccentricity.toFixed(4) : '—'),
    inclination: activeLiveOrbit?.inclination != null ? activeLiveOrbit.inclination.toFixed(2) : (orb ? orb.inclination.toFixed(2) : inclination),
    raan: activeLiveOrbit?.raan != null ? activeLiveOrbit.raan.toFixed(2) : (orb ? orb.RAAN.toFixed(2) : raan),
  };

  const orbitTypeLabel = isTleSat ? 'TLE' : (meta?.orbitTypeLabel ?? 'Keplerian');
  const orbitType = meta?.orbitType ?? 'inclined';
  const propagationStatus = getOrbitPropagationStatus(selectedSat, isTleSat);

  return (
    <>
      <div className={`${styles.panel} ${panelMinimized ? styles.panelMinimized : ''}`} style={propertyPanelStyle}>
        <div className={styles.header} {...dragHeaderProps}>
          <span className={styles.kindBadge}>SATELLITE</span>
          <span className={styles.satName}>{selectedName}</span>
          <div className={styles.headerActions}>
            <CustomToolTip title={panelMinimized ? 'Restore' : 'Minimize'}>
              <button className={styles.headerBtn} type="button" onClick={handleToggleMinimize}>
                <ToggleMinimizeSvg type={panelMinimized ? 'maximize' : 'minimize'} />
              </button>
            </CustomToolTip>
            <CustomToolTip title="Close">
              <button className={styles.headerBtn} type="button" onClick={handleClosePanel}><CloseSvg /></button>
            </CustomToolTip>
          </div>
        </div>

        {!panelMinimized && (
          <>
            <div className={styles.panelBody}>
              <CollapsibleSection title={`Orbital Details${orbitType === "inclined" ? "" : " · Keplerian"}`} badge={propagationStatus.badge} defaultOpen={isTleSat || liveMode}>
                {isTleSat ? (
                  <>
                    <PropertyRow label="Source" value={tleMeta.source} valueAlign="left" />
                    <PropertyRow label="TLE Line 1" value={tleMeta.line1} valueAlign="left" />
                    <PropertyRow label="TLE Line 2" value={tleMeta.line2} valueAlign="left" />
                  </>
                ) : liveMode ? (
                  <>
                    <PropertyRow label="Latitude" value={live.latitude} unit="°" isLive valueAlign="left" disabled={shouldBlockUI} />
                    <PropertyRow label="Longitude" value={live.longitude} unit="°" isLive valueAlign="left" />
                    <PropertyRow label="Altitude" value={live.altitude} unit="km" isLive valueAlign="left" />
                    <PropertyRow label="Velocity" value={live.velocity} unit="km/s" isLive valueAlign="left" />
                    <PropertyRow label="Period" value={period} unit="min" isLive valueAlign="left" />
                    <PropertyRow label="Semi-Major Axis" value={live.semiMajorAxis} unit="km" isLive valueAlign="left" />
                    <PropertyRow label="Eccentricity" value={live.eccentricity} isLive valueAlign="left" />
                    <PropertyRow label="Inclination" value={live.inclination} unit="°" isLive valueAlign="left" />
                    <PropertyRow label="RAAN" value={live.raan} unit="°" isLive valueAlign="left" />
                    <PropertyRow label="Mean Anomaly" value={liveMeanAnomaly} unit="°" isLive valueAlign="left" />
                  </>
                ) : (
                  <>
                    <PropertyRow label="Orbit Type" value={orbitTypeLabel} valueAlign="left" />
                    <PropertyRow label="Altitude" value={getField('altitude', altitude)} unit="km" inputType="number" valueAlign="left" onChange={v => setField('altitude', v)} disabled={shouldBlockUI} />
                    <PropertyRow label="Epoch" value={getField('epoch', epochDisplay)} inputType="datetime" valueAlign="left" onChange={v => setField('epoch', v)} disabled={shouldBlockUI} />
                    <PropertyRow label="Period" value={period} unit="min" isLive valueAlign="left" />

                    {orbitType === 'inclined' && <>
                      <PropertyRow label="Inclination" value={getField('inclination', inclination)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('inclination', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="RAAN" value={getField('raan', raan)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('raan', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="AOP" value={getField('aop', aop)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('aop', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="True Anomaly" value={getField('ta', ta)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('ta', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="Eccentricity" value={meta ? String(meta.eccentricity) : (orb ? orb.eccentricity.toFixed(6) : '—')} inputType="number" valueAlign="left" onChange={v => setField('eccentricity', v)} disabled={shouldBlockUI} />
                    </>}

                    {orbitType === 'sso' && <>
                      <PropertyRow label="Inclination" value={getField('inclination', inclination)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('inclination', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="LTAN" value={getField('ltan', meta?.ltan ?? '—')} inputType="text" valueAlign="left" onChange={v => setField('ltan', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="RAAN" value={getField('raan', raan)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('raan', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="AOP" value={getField('aop', aop)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('aop', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="True Anomaly" value={getField('ta', ta)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('ta', v)} disabled={shouldBlockUI} />
                    </>}

                    {orbitType === 'geo' && <>
                      <PropertyRow label="Longitude" value={getField('longitude', meta?.longitude_deg != null ? String(meta.longitude_deg) : '—')} unit="°" inputType="number" valueAlign="left" onChange={v => setField('longitude', v)} />
                      <PropertyRow label="Inclination" value={getField('inclination', inclination)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('inclination', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="Eccentricity" value={meta ? String(meta.eccentricity) : (orb ? orb.eccentricity.toFixed(6) : '—')} inputType="number" valueAlign="left" onChange={v => setField('eccentricity', v)} disabled={shouldBlockUI} />
                    </>}

                    {orbitType === 'heo' && <>
                      <PropertyRow label="Perigee Alt" value={getField('perigee', meta?.perigee_km != null ? String(meta.perigee_km) : '—')} unit="km" inputType="number" valueAlign="left" onChange={v => setField('perigee', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="Apogee Alt" value={getField('apogee', meta?.apogee_km != null ? String(meta.apogee_km) : '—')} unit="km" inputType="number" valueAlign="left" onChange={v => setField('apogee', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="Inclination" value={getField('inclination', inclination)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('inclination', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="RAAN" value={getField('raan', raan)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('raan', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="AOP" value={getField('aop', aop)} unit="°" inputType="number" valueAlign="left" onChange={v => setField('aop', v)} disabled={shouldBlockUI} />
                      <PropertyRow label="Eccentricity" value={meta ? String(meta.eccentricity) : (orb ? orb.eccentricity.toFixed(6) : '—')} inputType="number" valueAlign="left" onChange={v => setField('eccentricity', v)} disabled={shouldBlockUI} />
                    </>}
                  </>
                )}
                {SHOW_ORBIT_COLOR_PICKER && (
                  <div className={styles.colorRow}>
                    <span className={styles.colorLabel}>Orbit Color</span>
                    <div className={styles.colorPickerGrid}>
                      {MODELLING_ORBIT_COLORS.map(color => {
                        const colorArray = color.replace("[", "").replace("]", "").split(",").map(Number);
                        const rgbString = `rgba(${colorArray[0]},${colorArray[1]},${colorArray[2]})`;
                        const currentSelectedColor = getField('orbitColour', selectedSat?.orbitColour || MODELLING_DEFAULT_ORBIT_COLOR);
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setField('orbitColour', color)}
                            className={`${styles.colorPickerButton} ${currentSelectedColor === color ? styles.colorSelected : ''}`}
                            style={{ backgroundColor: rgbString }}
                            disabled={shouldBlockUI}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}
              </CollapsibleSection>

              <CollapsibleSection title="Physical Characteristics" defaultOpen={false}>
                <PropertyRow label="Mass" value={getField('mass', physDisplay.mass)} unit="kg" inputType="number" onChange={v => setField('mass', v)} disabled={shouldBlockUI} />
                <PropertyRow label="Volume" value={getField('volume', physDisplay.volume)} unit="m³" inputType="number" onChange={v => setField('volume', v)} disabled={shouldBlockUI} />
                <PropertyRow label="Coeff. of Drag" value={getField('dragCoeff', physDisplay.dragCoeff)} inputType="number" onChange={v => setField('dragCoeff', v)} disabled={shouldBlockUI} />
                <PropertyRow label="Drag Surface Area" value={getField('dragArea', physDisplay.dragArea)} unit="m²" inputType="number" onChange={v => setField('dragArea', v)} disabled={shouldBlockUI} />
                <PropertyRow label="Coeff. of Reflectivity" value={getField('reflectCoeff', physDisplay.reflectCoeff)} inputType="number" onChange={v => setField('reflectCoeff', v)} disabled={shouldBlockUI} />
                <PropertyRow label="SRP Surface Area" value={getField('srpArea', physDisplay.srpArea)} unit="m²" inputType="number" onChange={v => setField('srpArea', v)} disabled={shouldBlockUI} />
              </CollapsibleSection>

              <CollapsibleSection title="Subsystems & Sensors">
                <BusSection
                  onAddSensor={handleAddSensor}
                  onEditSensor={handleEditSensor}
                  onPasteSensor={handlePasteSensor}
                  sensors={sensors}
                  onRemoveSensor={handleRemoveSensor}
                  onCopySensor={handleCopySensor}
                  sensorClipboard={sensorClipboard}
                  disabled={shouldBlockUI}
                />
              </CollapsibleSection>
            </div>

            {isDirty && (
              <div className={styles.actionFooter}>
                <button className={styles.cancelActionBtn} onClick={handleCancel} type="button" disabled={saving}>Cancel</button>
                <button className={styles.updateBtn} onClick={handleUpdate} type="button" disabled={saving}>
                  {saving ? 'Updating...' : 'Update'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {!panelMinimized && sensorPanelOpen && (
        <SensorDetailsPanel
          onSave={handleSaveSensor}
          onClose={handleSensorPanelClose}
          mode={sensorPanelMode}
          initialData={editingSensorIndex !== null ? sensors[editingSensorIndex] : undefined}
          pasteOptions={sensorClipboard || []}
          onPreviewChange={handleSensorPreviewChange}
          style={sensorPanelStyle}
          existingNames={sensors.map(s => s.name?.trim().toLowerCase()).filter((n): n is string => !!n)}
          existingSensors={sensors}
        />
      )}
    </>
  );
};

export default PropertyPanel;
